/**
 * useOfferViability
 *
 * Fetches the deal viability insight for the amount currently in the offer field.
 *
 * Deliberately not live per keystroke. On every change the insight is hidden immediately and
 * the timer restarts; it reappears only once the investor has stopped typing. The product
 * owner asked for a clear "you're done typing, here's your answer" moment rather than a
 * message that flickers with every character — and a half-typed amount produces a real but
 * meaningless figure ("$3" is 0% of any floor).
 */

import { useEffect, useState } from 'react'
import { useQuery } from '@tanstack/react-query'

import { getOfferViability, type OfferViability } from '@/features/offers/api/offers'

/**
 * How long the investor must stop typing before the insight recomputes and reappears.
 *
 * The number spec suggested ~150ms; design review settled on 600ms so it reliably lands
 * after typing stops rather than mid-number.
 */
export const VIABILITY_DEBOUNCE_MS = 600

export interface UseOfferViabilityResult {
  /** The insight to render, or undefined while typing or in flight. */
  insight: OfferViability | undefined
  /** The amount `insight` was computed for — the copy must not quote the live input. */
  settledPrice: number | undefined
}

export function useOfferViability(
  token: string | null | undefined,
  propertyId: string | undefined,
  offerPrice: number | undefined
): UseOfferViabilityResult {
  const [settledPrice, setSettledPrice] = useState<number | undefined>(undefined)

  useEffect(() => {
    // Hide on every change, then wait out the window before asking for a new answer.
    setSettledPrice(undefined)

    if (!offerPrice || offerPrice <= 0) return

    const timer = setTimeout(() => setSettledPrice(offerPrice), VIABILITY_DEBOUNCE_MS)
    return () => clearTimeout(timer)
  }, [offerPrice])

  const { data } = useQuery({
    queryKey: ['offerViability', propertyId, settledPrice],
    queryFn: () => getOfferViability(token!, propertyId!, settledPrice!),
    enabled: !!token && !!propertyId && !!settledPrice,
    // Long enough that re-typing an amount is free, but finite: the curve is static config,
    // yet the answer also depends on the property. An agent can change the floor price, share
    // it, or have a valuation backfilled while the investor sits on this page, and nothing
    // invalidates this key. Caching forever would pin a stale answer — including a `show:false`
    // taken before the property had a valuation at all.
    staleTime: 60_000,
    // A failed lookup shows nothing rather than an error; this is helper content.
    retry: false,
  })

  return {
    insight: settledPrice ? data : undefined,
    settledPrice,
  }
}
